import { createSelector } from '../reselect';
import {
  total,
  simpleChart,
  simpleChart2,
  simpleChart3,
  simpleChartMinus,
  getNewStatsService,
} from '../generalUtils';

const getNewStats = getNewStatsService('klaviyo');

import { chartOrders, chartSales, totalOrders, totalSales } from '../generalSelectors';

//
const totalKlaviyoClickedEmail = createSelector([getNewStats], (stats) => {
  return total(stats, 'clickedEmail');
});

const chartKlaviyoClickedEmail = createSelector([getNewStats], (stats) => {
  return simpleChart(stats, 'clickedEmail', { fill24: true });
});
const totalKlaviyoReceivedEmail = createSelector([getNewStats], (stats) => {
  return total(stats, 'receivedEmail');
});

const chartKlaviyoReceivedEmail = createSelector([getNewStats], (stats) => {
  return simpleChart(stats, 'receivedEmail', { fill24: true });
});

const totalKlaviyoClickedSms = createSelector([getNewStats], (stats) => {
  return total(stats, 'clickedSms');
});

const chartKlaviyoClickedSms = createSelector([getNewStats], (stats) => {
  return simpleChart(stats, 'clickedSms', { fill24: true });
});

const totalKlaviyoUnsubscribed = createSelector([getNewStats], (stats) => {
  return total(stats, 'unsubscribed');
});

const chartKlaviyoUnsubscribed = createSelector([getNewStats], (stats) => {
  return simpleChart(stats, 'unsubscribed', { fill24: true });
});

const totalKlaviyoSubscribed = createSelector([getNewStats], (stats) => {
  return total(stats, 'subscribedToList');
});

const totalKlaviyoPlacedOrder = createSelector([getNewStats], (stats) => {
  return total(stats, 'conversionValue');
});

const totalKlaviyoPlacedOrderTotalPriceFlows = createSelector([getNewStats], (stats) => {
  return total(stats, 'flowsConversionValue');
});

const chartKlaviyoPlacedOrderTotalPriceFlows = createSelector([getNewStats], (stats) => {
  return simpleChart(stats, 'flowsConversionValue', { fill24: true });
});

const totalKlaviyoPlacedOrderTotalPriceCampaigns = createSelector([getNewStats], (stats) => {
  return total(stats, 'campaignsConversionValue');
});

const chartKlaviyoPlacedOrderTotalPriceCampaigns = createSelector([getNewStats], (stats) => {
  return simpleChart(stats, 'campaignsConversionValue', { fill24: true });
});

const totalKlaviyoSubscribedToList = createSelector([getNewStats], (stats) => {
  return total(stats, 'subscribedToList');
});

const chartKlaviyoSubscribedToList = createSelector([getNewStats], (stats) => {
  return simpleChart(stats, 'subscribedToList', { fill24: true });
});

const totalKlaviyoNetNewSubscribers = createSelector(
  [totalKlaviyoSubscribedToList, totalKlaviyoUnsubscribed],
  (added, unsub) => (added || 0) - (unsub || 0)
);

const chartKlaviyoNetNewSubscribers = createSelector(
  [chartKlaviyoSubscribedToList, chartKlaviyoUnsubscribed],
  (SubscribedToList, Unsubscribed) => {
    var chart1 = SubscribedToList;
    var chart2 = Unsubscribed;
    return simpleChartMinus(chart1, chart2);
  }
);

const totalKlaviyoPlacedOrderSalesPercentFlows = createSelector(
  [totalSales, totalKlaviyoPlacedOrderTotalPriceFlows],
  (sales, flows) => (!sales ? 0 : (100 * flows) / sales)
);

const chartKlaviyoPlacedOrderSalesPercentFlows = createSelector(
  [chartKlaviyoPlacedOrderTotalPriceFlows, chartSales],
  (chartKlaviyoPlacedOrderTotalPriceFlows, chartSales) => {
    var chart1 = chartKlaviyoPlacedOrderTotalPriceFlows;
    var chart2 = chartSales;
    return simpleChart2(chart1, chart2, { chart1yMultiplier: 100 });
  }
);
const totalKlaviyoPlacedOrderSalesPercentCampaigns = createSelector(
  [totalSales, totalKlaviyoPlacedOrderTotalPriceFlows, totalKlaviyoPlacedOrderTotalPriceCampaigns],
  (sales, flows, campaigns) => (!sales ? 0 : (100 * campaigns) / sales)
);

const chartKlaviyoPlacedOrderSalesPercentCampaigns = createSelector(
  [chartKlaviyoPlacedOrderTotalPriceCampaigns, chartSales],
  (chartKlaviyoPlacedOrderTotalPriceCampaigns, chartSales) => {
    var chart1 = chartKlaviyoPlacedOrderTotalPriceCampaigns;
    var chart2 = chartSales;
    return simpleChart2(chart1, chart2, { chart1yMultiplier: 100 });
  }
);
const klaviyoPlacedOrderSales = createSelector(
  [totalSales, totalKlaviyoPlacedOrderTotalPriceFlows, totalKlaviyoPlacedOrderTotalPriceCampaigns],
  (sales, flows, campaigns) => (!sales ? 0 : flows + campaigns)
);

const totalKlaviyoPlacedOrderSalesPercent = createSelector(
  [totalSales, totalKlaviyoPlacedOrderTotalPriceFlows, totalKlaviyoPlacedOrderTotalPriceCampaigns],
  (sales, flows, campaigns) => (!sales ? 0 : (100 * (flows + campaigns)) / sales)
);

const chartKlaviyoPlacedOrderSalesPercent = createSelector(
  [chartKlaviyoPlacedOrderSalesPercentFlows, chartKlaviyoPlacedOrderSalesPercentCampaigns],
  (chartKlaviyoPlacedOrderSalesPercentFlows, chartKlaviyoPlacedOrderSalesPercentCampaigns) => {
    var chart1 = chartKlaviyoPlacedOrderSalesPercentFlows;
    var chart2 = chartKlaviyoPlacedOrderSalesPercentCampaigns;
    return simpleChart3(chart1, chart2, []);
  }
);

const totalKlaviyoOpenedEmail = createSelector([getNewStats], (stats) =>
  total(stats, 'openedEmail')
);

const klaviyoEmailOpenRate = createSelector(
  [totalKlaviyoReceivedEmail, totalKlaviyoOpenedEmail],
  (totalKlaviyoReceivedEmail, totalKlaviyoOpenedEmail) => {
    if (!totalKlaviyoOpenedEmail) return 0;
    var per = (totalKlaviyoOpenedEmail * 100) / totalKlaviyoReceivedEmail;
    return per > 100 ? 100 : per;
  }
);
const klaviyoCVR = createSelector(
  //Purchase number from Shopify / Received Emails
  [totalOrders, totalKlaviyoReceivedEmail],
  (orders, receivedEmails) => {
    if (!receivedEmails || receivedEmails === 0) return 0;

    return orders / receivedEmails;
  }
);

const chartKlaviyoCVR = createSelector(
  [chartOrders, chartKlaviyoReceivedEmail],
  (orders, receivedEmails) => {
    var chart1 = orders;
    var chart2 = receivedEmails;
    return simpleChart2(chart1, chart2, { chart1yMultiplier: 100 });
  }
);

const klaviyoRevenuePerEmail = createSelector(
  //Revenue per email - (Shopify sales / Received Emails)
  [totalSales, totalKlaviyoReceivedEmail],
  (sales, receivedEmails) => {
    if (!receivedEmails || receivedEmails === 0) return 0;

    return sales / receivedEmails;
  }
);

const chartKlaviyoRevenuePerEmail = createSelector(
  [chartSales, chartKlaviyoReceivedEmail],
  (sales, receivedEmails) => {
    var chart1 = sales;
    var chart2 = receivedEmails;
    return simpleChart2(chart1, chart2, { chart1yMultiplier: 100 });
  }
);

// MAINLY FOR updateDayStats
const PROPS_TO_SELECTORS = {};

export default {
  PROPS_TO_SELECTORS,
  SELECTORS: {
    klaviyoCVR,
    chartKlaviyoCVR,
    klaviyoRevenuePerEmail,
    chartKlaviyoRevenuePerEmail,
    totalKlaviyoPlacedOrderTotalPriceFlows,
    totalKlaviyoPlacedOrderTotalPriceCampaigns,
    totalKlaviyoPlacedOrderSalesPercent,
    totalKlaviyoOpenedEmail,
    klaviyoEmailOpenRate,
    totalKlaviyoPlacedOrderSalesPercentFlows,
    totalKlaviyoPlacedOrderSalesPercentCampaigns,
    chartKlaviyoPlacedOrderTotalPriceFlows,
    chartKlaviyoPlacedOrderSalesPercentFlows,
    chartKlaviyoPlacedOrderTotalPriceCampaigns,
    chartKlaviyoPlacedOrderSalesPercentCampaigns,
    chartKlaviyoPlacedOrderSalesPercent,
    chartKlaviyoUnsubscribed,
    chartKlaviyoClickedEmail,
    chartKlaviyoClickedSms,
    totalKlaviyoClickedEmail,
    totalKlaviyoClickedSms,
    klaviyoPlacedOrderSales,
    totalKlaviyoPlacedOrder,
    totalKlaviyoSubscribed,

    totalKlaviyoSubscribedToList,
    chartKlaviyoSubscribedToList,
    totalKlaviyoNetNewSubscribers,
    chartKlaviyoNetNewSubscribers,
    chartKlaviyoReceivedEmail,
    totalKlaviyoReceivedEmail,
    totalKlaviyoUnsubscribed,
  },
};
